import { ChangeEvent, useState } from 'react'
import { DeleteImage, UploadImage } from '~/api'
import { COLORS } from '~/constants/colors'
import $sentry from '~/lib/sentry'

import NextImage from 'next/image'
import Icon from '~/components/Icon'
import { IconLoaderCircle } from '~/components/Icon/IconLoaderCircle'
import { useGlobal } from '../Common/GlobalDataProvider'
import { localStore, StoreKey } from '~/utils/local-store'
interface ImageUploaderI {
    className?: string
    image?: string
    setImage?: (value: string) => void
    localeText: any
    containerName: string
    onAddClick?: () => void
}

export const ImageUploader = ({
    className,
    image,
    setImage = () => false,
    localeText,
    containerName,
    onAddClick
}: ImageUploaderI) => {
    const { toastProps, setToastProps } = useGlobal()
    const [uploading, setUploading] = useState(false)
    const [removing, setRemoving] = useState(false)
    const [preview, setPreview] = useState(image || '')

    const inputChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (uploading) {
            return
        }

        if (!e.target.files) {
            return
        }

        const formData = new FormData()

        const imageList = Array.from(e.target.files)

        let limitExceeded = false

        // Generate filename with businessId and random number
        const businessIdObj = localStore.get(StoreKey.SELECTED_BUSINESS)
        const businessId = businessIdObj?.data
        const randomNum = Math.floor(100000 + Math.random() * 900000)
        imageList.forEach((item) => {
            if (item instanceof File) {
                const lastDot = item.name.lastIndexOf('.')
                const name = lastDot !== -1 ? item.name.substring(0, lastDot) : item.name
                const ext = lastDot !== -1 ? item.name.substring(lastDot) : ''
                const newFileName = businessId
                    ? `${name}_${businessId}_${randomNum}${ext}`
                    : `${name}_${randomNum}${ext}`
                const newFile = new File([item], newFileName, { type: item.type })
                formData.append('image', newFile)
            } else {
                formData.append('image', item)
            }

            // image size more than 5mb
            if (item.size > 5000000) {
                limitExceeded = true
            }
        })

        formData.append('container_name', containerName)

        if (limitExceeded) {
            setToastProps({
                ...toastProps,
                open: true,
                type: 'error',
                message: localeText?.imageUploadMessage?.uploadSize
            })
            return
        }

        setUploading(true)

        const { data } = await UploadImage(formData, localeText)

        setUploading(false)

        if (!data) {
            setToastProps({
                ...toastProps,
                open: true,
                type: 'error',
                message: localeText?.imageUploadMessage?.failedMessage
            })
            return
        }

        setToastProps({
            ...toastProps,
            open: true,
            type: 'success',
            message: localeText?.imageUploadMessage?.successUpload
        })

        setImage(data.data)
        setPreview(URL.createObjectURL(e.target.files[0]))
    }

    const remove = async () => {
        if (removing) {
            return
        }

        if (image === '' || !image) {
            return
        }

        setRemoving(true)

        const { data } = await DeleteImage(image, localeText)

        setRemoving(false)

        if (!data) {
            setToastProps({
                ...toastProps,
                open: true,
                type: 'error',
                message: localeText?.imageUploadMessage?.failedUIError
            })
            return
        }

        setToastProps({
            ...toastProps,
            open: true,
            type: 'success',
            message: localeText?.imageUploadMessage?.SuccessErrorImage
        })
        setImage('')
        setPreview('')
    }

    return (
        <div className={className}>
            <div
                onClick={() => {
                    if (onAddClick && (image === '' || !image || image === 'null')) {
                        onAddClick()
                    }
                }}
                className="w-20 h-20 relative justify-center items-center cursor-pointer"
            >
                {image === '' || !image || image === 'null' ? (
                    <>
                        <NextImage
                            src="/images/add-image.svg"
                            width={80}
                            height={80}
                            alt="default"
                            unoptimized></NextImage>
                        {!onAddClick && (
                            <input
                                type="file"
                                accept="image/*"
                                multiple={false}
                                className="w-full h-full absolute left-0 top-0 cursor-pointer opacity-0"
                                onChange={inputChange}></input>
                        )}
                        {uploading && (
                            <div className="absolute w-full h-full left-0 top-0 bg-opacity-80 rounded-lg bg-neutrals-900 flex justify-center items-center">
                                <IconLoaderCircle className="w-10 h-10"></IconLoaderCircle>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="flex-wrap relative w-20 h-20">
                            <div className="rounded-md flex items-center justify-center bg-[#FFF1EB] w-20 h-20 overflow-hidden">
                                <NextImage
                                    src={preview}
                                    key={image}
                                    width="80"
                                    height="80"
                                    unoptimized
                                    objectFit="cover"
                                    alt="image"
                                    onError={() => {
                                        $sentry.sendSentryEvent('Error loading image: ' + image)
                                    }}></NextImage>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple={false}
                                    className="w-full h-full absolute left-0 top-0 cursor-pointer opacity-0"
                                    onChange={inputChange}></input>
                            </div>
                            <span className="absolute -top-1 -right-1 bg-neutrals-light h-5 w-5">
                                {removing ? (
                                    <IconLoaderCircle className="w-5 h-5"></IconLoaderCircle>
                                ) : (
                                    <Icon
                                        name="trash-outline"
                                        size="lg"
                                        onClick={() => setImage("")}
                                        color={COLORS.SEMANTIC.RED[500]}></Icon>
                                )}
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
